import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { pdfService } from '../../../services/pdfService';
import { openaiService } from '../../../services/openaiService';

const LeadDetailModal = ({ lead, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [editMode, setEditMode] = useState(false);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [analyzingPDF, setAnalyzingPDF] = useState(false);
  const [pdfAnalysis, setPdfAnalysis] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nombre: lead?.nombre,
    email: lead?.email,
    telefono: lead?.telefono,
    presupuesto: lead?.presupuesto,
    proyectoInteres: lead?.proyectoInteres,
    stage: lead?.stage,
    nivelInteres: lead?.nivelInteres
  });

  const stageOptions = [
    { value: 'new', label: 'Nuevo' },
    { value: 'contacted', label: 'Contactado' },
    { value: 'qualified', label: 'Calificado' },
    { value: 'proposal', label: 'Propuesta' },
    { value: 'negotiation', label: 'Negociación' },
    { value: 'closed_won', label: 'Ganado' },
    { value: 'closed_lost', label: 'Perdido' }
  ];

  const interestLevelOptions = [
    { value: 'hot', label: 'Alto interés' },
    { value: 'warm', label: 'Interés medio' },
    { value: 'cold', label: 'Bajo interés' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handleSave = () => {
    onSave({ ...lead, ...formData });
    setEditMode(false);
  };

  const handlePDFUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const validation = pdfService?.validatePDF(file);
    if (!validation?.valid) {
      alert(validation?.error);
      return;
    }

    try {
      setUploadingPDF(true);
      setAnalyzingPDF(true);

      // Convert PDF to image for AI analysis
      const imageUrl = await pdfService?.pdfToImage(file);

      // Analyze PDF with AI
      const analysis = await openaiService?.analyzeLeadDocument(imageUrl, lead);

      setPdfAnalysis(analysis);
      setUploadedFiles(prev => [...prev, {
        name: file?.name,
        size: file?.size,
        uploadedAt: new Date()?.toISOString(),
        analysis: analysis
      }]);

      // Update notes with AI insights
      const aiInsights = `\n\n--- Análisis de Documento (${new Date()?.toLocaleString('es-PE')}) ---\n${analysis?.summary}\n\nPuntos Clave:\n${analysis?.keyPoints?.map(p => `• ${p}`)?.join('\n')}`;
      
      onSave({ 
        ...lead, 
        notas: (lead?.notas || '') + aiInsights 
      });

    } catch (error) {
      console.error('Error analyzing PDF:', error);
      alert('Error al analizar el documento: ' + error?.message);
    } finally {
      setUploadingPDF(false);
      setAnalyzingPDF(false);
    }
  };

  const tabs = [
    { id: 'info', label: 'Información', icon: 'User' },
    { id: 'documents', label: 'Documentos', icon: 'FileText' },
    { id: 'history', label: 'Historial', icon: 'Clock' },
    { id: 'tasks', label: 'Tareas', icon: 'CheckSquare' },
    { id: 'notes', label: 'Notas', icon: 'StickyNote' }
  ];

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 md:p-6 border-b border-border gap-4">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
              <span className="text-primary font-medium text-sm sm:text-base">
                {lead?.nombre?.split(' ')?.map(n => n?.[0])?.join('')?.substring(0, 2)}
              </span>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground font-heading truncate">
                {lead?.nombre}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{lead?.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="X"
                  iconPosition="left"
                  onClick={() => setEditMode(false)}
                  className="flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Cancelar</span>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSave}
                  className="flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Guardar</span>
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                onClick={() => setEditMode(true)}
                className="flex-1 sm:flex-none"
              >
                <span className="hidden sm:inline">Editar</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
              className="flex-shrink-0"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border overflow-x-auto">
          <div className="flex px-4 md:px-6 min-w-max">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-3 border-b-2 transition-smooth touch-target ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Upload" size={20} className="text-primary" />
                  Subir Documento PDF
                </h3>
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-6 sm:p-8 text-center hover:border-primary transition-colors cursor-pointer touch-target"
                  onClick={() => fileInputRef?.current?.click()}
                >
                  <Icon name="FileUp" size={40} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-2">
                    {uploadingPDF ? 'Subiendo...' : 'Haz clic para seleccionar un PDF'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    La IA analizará el documento automáticamente
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePDFUpload}
                  className="hidden"
                  disabled={uploadingPDF}
                />
              </div>

              {analyzingPDF && (
                <div className="p-4 bg-primary/10 border border-primary rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin">
                      <Icon name="Loader" size={20} className="text-primary" />
                    </div>
                    <p className="text-sm font-medium text-primary">
                      Analizando documento con IA...
                    </p>
                  </div>
                </div>
              )}

              {pdfAnalysis && (
                <div className="p-4 bg-success/10 border border-success rounded-lg">
                  <h4 className="text-sm font-semibold text-success mb-2 flex items-center gap-2">
                    <Icon name="CheckCircle" size={16} />
                    Análisis Completado
                  </h4>
                  <p className="text-sm text-foreground mb-3">{pdfAnalysis?.summary}</p>
                  {pdfAnalysis?.keyPoints?.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Puntos Clave:</p>
                      {pdfAnalysis?.keyPoints?.map((point, idx) => (
                        <p key={idx} className="text-xs text-foreground flex items-start gap-2">
                          <Icon name="Check" size={12} className="text-success flex-shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {uploadedFiles?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Documentos Subidos</h4>
                  <div className="space-y-2">
                    {uploadedFiles?.map((file, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Icon name="FileText" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{file?.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {(file?.size / 1024 / 1024)?.toFixed(2)} MB • {new Date(file?.uploadedAt)?.toLocaleDateString('es-PE')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'info' && (
            <div className="space-y-6">
              {editMode ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Nombre Completo"
                    type="text"
                    value={formData?.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e?.target?.value })}
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={formData?.email}
                    onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  />
                  <Input
                    label="Teléfono"
                    type="tel"
                    value={formData?.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e?.target?.value })}
                  />
                  <Input
                    label="Presupuesto (PEN)"
                    type="number"
                    value={formData?.presupuesto}
                    onChange={(e) => setFormData({ ...formData, presupuesto: e?.target?.value })}
                  />
                  <Input
                    label="Proyecto de Interés"
                    type="text"
                    value={formData?.proyectoInteres}
                    onChange={(e) => setFormData({ ...formData, proyectoInteres: e?.target?.value })}
                  />
                  <Select
                    label="Etapa"
                    options={stageOptions}
                    value={formData?.stage}
                    onChange={(value) => setFormData({ ...formData, stage: value })}
                  />
                  <Select
                    label="Nivel de Interés"
                    options={interestLevelOptions}
                    value={formData?.nivelInteres}
                    onChange={(value) => setFormData({ ...formData, nivelInteres: value })}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Teléfono</div>
                    <div className="text-sm text-foreground">{lead?.telefono}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Fuente</div>
                    <div className="text-sm text-foreground capitalize">{lead?.fuente}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Proyecto de Interés</div>
                    <div className="text-sm text-foreground">{lead?.proyectoInteres}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Presupuesto</div>
                    <div className="text-sm text-foreground">{formatCurrency(lead?.presupuesto)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Etapa</div>
                    <div className="text-sm text-foreground capitalize">{lead?.stageLabel}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Nivel de Interés</div>
                    <div className="text-sm text-foreground capitalize">{lead?.nivelInteresLabel}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Última Actividad</div>
                    <div className="text-sm text-foreground">{formatDate(lead?.ultimaActividad)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Próximo Seguimiento</div>
                    <div className="text-sm text-foreground">{lead?.proximoSeguimiento}</div>
                  </div>
                </div>
              )}

              <div className="pt-6 border-t border-border">
                <h3 className="text-lg font-semibold text-foreground font-heading mb-4">
                  Preferencias de Propiedad
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-xl p-4">
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Tipo</div>
                    <div className="text-sm text-foreground">{lead?.preferencias?.tipo}</div>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-4">
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Habitaciones</div>
                    <div className="text-sm text-foreground">{lead?.preferencias?.habitaciones}</div>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-4">
                    <div className="text-xs text-muted-foreground mb-1 font-caption">Área Mínima</div>
                    <div className="text-sm text-foreground">{lead?.preferencias?.areaMinima} m²</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {lead?.historial?.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 pb-4 border-b border-border last:border-0">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${item?.color}20` }}
                  >
                    <Icon name={item?.icon} size={20} color={item?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-foreground">{item?.accion}</div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                        {formatDate(item?.fecha)}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">{item?.descripcion}</div>
                    {item?.usuario && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Por: {item?.usuario}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              {lead?.tareas?.map((task, index) => (
                <div key={index} className="bg-muted/50 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      <Icon 
                        name={task?.completada ? 'CheckCircle2' : 'Circle'} 
                        size={20} 
                        className="mr-2"
                        color={task?.completada ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                      <div className={`font-medium ${task?.completada ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                        {task?.titulo}
                      </div>
                    </div>
                    <div className={`
                      text-xs px-2 py-1 rounded-lg
                      ${task?.prioridad === 'alta' ? 'bg-error/10 text-error' :
                        task?.prioridad === 'media'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'}
                    `}>
                      {task?.prioridad === 'alta' ? 'Alta' : task?.prioridad === 'media' ? 'Media' : 'Baja'}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">{task?.descripcion}</div>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Icon name="Calendar" size={14} className="mr-1" />
                    <span>Vencimiento: {formatDate(task?.fechaVencimiento)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {lead?.notas?.map((note, index) => (
                <div key={index} className="bg-muted/50 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-foreground">{note?.titulo}</div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                      {formatDate(note?.fecha)}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground whitespace-pre-line">
                    {note?.contenido}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Por: {note?.autor}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;